import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import moment from 'moment';

const styles = theme => ({
  root: {
    position: 'absolute',
    minHeight: '3.5%',
    cursor: 'pointer',
  },
  event: {
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.facet.accentBlue,
    padding: theme.spacing(0.25, 1, 0.25, 1),
    borderRadius: '7px',
    height: '100%',
    justifyContent: 'space-between',
    ...theme.typography.body1,
  },
  selectedEvent: {
    backgroundColor: theme.palette.facet.accentBlue4,
  },
  timeText: {
    fontWeight: 'bold',
  },
  eventBubble: {
    display: 'flex',
    minWidth: '14px',
    height: '14px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.typography.body1,
  },
  lessThanThirtyMinutes: {
    overflow: 'hidden',
  },
});

const ScheduleEvent = ({
  className,
  classes,
  startTime,
  endTime,
  title,
  windowStart,
  windowEnd,
  offset30,
  offset60,
  numOverlaps,
  numOverlapsBefore,
  actionItemLinkCount,
  onClick,
  isSelected,
}) => {
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const minStart = moment.max(windowStart, startMoment);
  const maxEnd = moment.min(windowEnd, endMoment);

  // Marcus said to ignore anything past 20 overlapping events
  if (numOverlapsBefore >= 20) {
    return null;
  }

  const LessThanThirtyMinutesDisplay = () => (
    <div className={classes.lessThanThirtyMinutes}>
      <span className={classes.timeText}>
        {`${startMoment.format('h:mm')} - ${endMoment.format('h:mm')} | `}
      </span>
      <span>{title}</span>
    </div>
  );

  const MoreThanThirtyMinutesDisplay = () => (
    <div>
      <div className={classes.timeText}>
        {`${startMoment.format('h:mm')} - ${endMoment.format('h:mm')}`}
      </div>
      <div>{title}</div>
    </div>
  );

  return (
    <div
      className={classNames(classes.root, className)}
      role="presentation"
      onClick={onClick}
      style={{
        height: `${
          ((maxEnd.valueOf() - minStart.valueOf())
            / (windowEnd.valueOf() - windowStart.valueOf() + offset60))
          * 100
        }%`,
        top: `${
          ((minStart.valueOf() - windowStart.valueOf() + offset30)
            / (windowEnd.valueOf() - windowStart.valueOf() + offset60))
          * 100
        }%`,
        width: `${100 / (numOverlaps + 1)}%`,
        left: `${(numOverlapsBefore / (numOverlaps + 1)) * 100}%`,
      }}
    >
      <div className={classNames(classes.event, {
        [classes.selectedEvent]: isSelected,
      })}
      >
        {endMoment.diff(startMoment, 'minute') <= 30
          ? <LessThanThirtyMinutesDisplay />
          : <MoreThanThirtyMinutesDisplay />}
        <div className={classes.eventBubble}>{actionItemLinkCount}</div>
      </div>
    </div>
  );
};

ScheduleEvent.defaultProps = {
  className: '',
  actionItemLinkCount: 0,
};

ScheduleEvent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired,
  offset30: PropTypes.number.isRequired,
  offset60: PropTypes.number.isRequired,
  numOverlaps: PropTypes.number.isRequired,
  numOverlapsBefore: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  actionItemLinkCount: PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
};

export default withStyles(styles)(ScheduleEvent);
